import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';
import { useSelector } from 'react-redux';
import { useMatch } from 'react-router-dom';

import { useFeatureFlag } from 'shared/feature-flags';
import { RootState } from 'store/store';
import UDTabs from 'ud-ui/components/tabs';
import { ChampionshipEditTab } from 'modules/championship/domain/enums/championship-edit-tab';
import { ChampionshipStateData } from 'modules/championship/store/championship/index';
import { stageSelectors } from 'modules/championship/store/stages';
import { useCloseSymbolicTeamForm } from 'modules/championship/ui/hooks/use-close-symbolic-team-form';
import { isChampionshipEditTabAvailable } from 'modules/championship/domain/utils/application-campaign-tab-availability';
import { getChampionshipEditTabs } from './constants';

const APPLICATION_CAMPAIGN_TOOLTIP = 'Сначала заполните и сохраните заявочную компанию на вкладке «Параметры»';

type ChampionshipEditFormTabsProps = {
  championship?: ChampionshipStateData;
};

const ChampionshipEditFormTabs = ({ championship }: ChampionshipEditFormTabsProps) => {
  const { t } = useLingui();
  const isFlow1447 = useFeatureFlag('applicationsFlow1447');

  // Легаси-версия «Заявок» (флаг `applicationsFlow1447` выключен) открывает состав команды
  // отдельной страницей `applications/:teamId`, а не модалкой — вкладке нужен сабл-лейбл с
  // именем открытой команды, как было до IMGM-1447. При включённом флаге состав — оверлей над
  // списком, роут не матчится, и этот сабл-лейбл не нужен вовсе.
  const teamRouteMatch = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/applications/:teamId`);
  const isTeam = !isFlow1447 ? teamRouteMatch : null;

  const isSymbolicTeams = useMatch(`${Pages.CHAMPIONSHIPS.EDIT}/symbolic-teams`);

  const isStageCreate = useMatch(Pages.CHAMPIONSHIPS.STEPS.CREATE);
  const stageRoute = useMatch(`${Pages.CHAMPIONSHIPS.STEPS.STAGE}/*`);

  // `/steps/create` тоже подходит под шаблон `:stageId`, поэтому сначала отсекаем
  // создание, а id этапа берём только если это действительно число.
  const routeStageId = Number(stageRoute?.params.stageId);
  const isStageRoute = !isStageCreate && Number.isInteger(routeStageId) && routeStageId > 0;
  // Читаем этап по id из адреса, а не через `selectedStageSelector`: подпись не
  // должна зависеть от того, успел ли стор догнать роут.
  const stage = useSelector((state: RootState) => (isStageRoute ? stageSelectors.selectById(state, routeStageId) : undefined));

  // Тот же колбэк, что гасит форму при уходе с вкладки, — чтобы закрытие по
  // клику на подпись и сброс на размонтировании экрана шли одним путём.
  const closeSymbolicTeamForm = useCloseSymbolicTeamForm(championship?.id);

  let subLabel: string | undefined;
  let urlTab: string | undefined;
  let action = () => {};

  if (isTeam) {
    urlTab = ChampionshipEditTab.TEAMS;
    subLabel = championship?.teams?.find((team) => team.id === Number(isTeam.params.teamId))?.name;
  }

  // Возврат к списку этапов даёт сама навигация по вкладке, поэтому `action` здесь не нужен.
  if (isStageCreate) {
    urlTab = ChampionshipEditTab.STEPS;
    subLabel = t`Добавление этапа`;
  } else if (stage) {
    urlTab = ChampionshipEditTab.STEPS;
    subLabel = stage.name;
  }

  if (isSymbolicTeams && championship?.showSymbolicTeamForm) {
    urlTab = ChampionshipEditTab.SYMBOLIC_TEAMS;
    subLabel = championship.selectedSymbolicTeam?.name || t`Добавление сборной`;
    action = closeSymbolicTeamForm;
  }

  const tabs = useMemo(() => {
    return getChampionshipEditTabs(isFlow1447).map((tab) => {
      // Гейт по заявочной компании — часть IMGM-1447, на `dev` его нет: вкладки там гасит
      // только загрузка соревнования. Легаси-ветка флага (`applicationsFlow1447` выключен)
      // должна вести себя как `dev`, поэтому вне флага проверку не делаем вовсе.
      const isAvailable = !isFlow1447 || isChampionshipEditTabAvailable(championship?.settings, tab.hash);

      return {
        ...tab,
        label: t(tab.label),
        subLabel: urlTab === tab.hash ? subLabel : undefined,
        onClick: urlTab === tab.hash ? action : undefined,
        disabled: !championship || !isAvailable,
        tooltip: isFlow1447 && championship && !isAvailable ? APPLICATION_CAMPAIGN_TOOLTIP : undefined,
      };
    });
    // `action` пересобирается на каждый рендер и в зависимости не идёт: он всегда
    // соответствует текущему `urlTab`, а тот в списке есть.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, urlTab, subLabel, championship, isFlow1447]);

  return <UDTabs tabs={tabs} defaultTab={tabs[0]} activeRoutePattern={Pages.CHAMPIONSHIPS.EDIT} />;
};

export default ChampionshipEditFormTabs;
